import React, { useState, useEffect, useCallback } from 'react';
import apiClient, { getS3Status, requeueS3Files } from '../services/api';
import { S3StatusResponse } from '../types';

interface Widget {
  title: string;
  image: string | null;
  error?: string;
  width: number;
  height: number;
  index: number;
}

type MonitoringTab = 'system' | 's3';

const MonitoringPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<MonitoringTab>('system');

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Monitoring</h1>

        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('system')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'system'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            System
          </button>
          <button
            onClick={() => setActiveTab('s3')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 's3'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            S3
          </button>
        </div>

        {activeTab === 'system' && <SystemTab />}
        {activeTab === 's3' && <S3Tab />}
      </div>
    </div>
  );
};

const SystemTab: React.FC = () => {
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchWidgets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/monitoring/widgets');
      const fetched: Widget[] = response.data.widgets || [];
      const bottomOrder = ['DLQ Messages', 'Lambda Errors', 'Lambda Duration (ms)'];
      const top = fetched.filter(w => !bottomOrder.includes(w.title));
      const bottom = bottomOrder
        .map(title => fetched.find(w => w.title === title))
        .filter((w): w is Widget => w !== undefined);
      setWidgets([...top, ...bottom]);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  useEffect(() => {
    const interval = setInterval(fetchWidgets, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWidgets]);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-gray-500">Last updated: {lastRefresh.toLocaleTimeString()}</span>
        <button
          onClick={fetchWidgets}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
          <span>{error}</span>
          <button onClick={fetchWidgets} className="ml-4 text-sm font-medium text-red-700 underline hover:text-red-900">
            Retry
          </button>
        </div>
      )}

      {loading && widgets.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading monitoring dashboard...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {widgets.map((widget) => (
            <div
              key={widget.index}
              className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden"
            >
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-700">{widget.title}</h3>
              </div>
              <div className="p-2">
                {widget.image ? (
                  <img
                    src={`data:image/png;base64,${widget.image}`}
                    alt={widget.title}
                    className="w-full h-auto"
                  />
                ) : (
                  <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                    {widget.error || 'No data available'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const S3Tab: React.FC = () => {
  const [status, setStatus] = useState<S3StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Track per-press requeue state: null = idle, 'loading' = in flight, string = result message
  const [requeueState, setRequeueState] = useState<Record<string, 'loading' | string>>({});
  const [requeueAllState, setRequeueAllState] = useState<'loading' | string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getS3Status();
      setStatus(data);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load S3 status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRequeue = async (press: string) => {
    setRequeueState(s => ({ ...s, [press]: 'loading' }));
    try {
      const result = await requeueS3Files(press);
      setRequeueState(s => ({ ...s, [press]: result.message }));
      // Refresh counts after a brief delay
      setTimeout(fetchStatus, 1500);
    } catch (err: any) {
      setRequeueState(s => ({ ...s, [press]: `Error: ${err.message}` }));
    }
  };

  const handleRequeueAll = async () => {
    if (!window.confirm(`Requeue all ${status?.total ?? 0} file(s) from S3 for reprocessing?`)) return;
    setRequeueAllState('loading');
    try {
      const result = await requeueS3Files();
      setRequeueAllState(result.message);
      setTimeout(fetchStatus, 1500);
    } catch (err: any) {
      setRequeueAllState(`Error: ${err.message}`);
    }
  };

  const anyBusy = requeueAllState === 'loading' || Object.values(requeueState).includes('loading');

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-gray-500">Last updated: {lastRefresh.toLocaleTimeString()}</span>
        <button
          onClick={fetchStatus}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
          <span>{error}</span>
          <button onClick={fetchStatus} className="ml-4 text-sm font-medium text-red-700 underline hover:text-red-900">
            Retry
          </button>
        </div>
      )}

      {loading && !status ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading S3 status...</p>
          </div>
        </div>
      ) : status ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Press</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Files in S3</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {status.presses.map(({ press, count }) => {
                const state = requeueState[press];
                return (
                  <tr key={press}>
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">{press}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <span className={count > 0 ? 'font-semibold text-amber-600' : 'text-gray-400'}>
                        {count}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {state && state !== 'loading' ? (
                        <span className={state.startsWith('Error') ? 'text-red-600' : 'text-green-600'}>
                          {state}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleRequeue(press)}
                          disabled={count === 0 || anyBusy}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {state === 'loading' ? 'Queuing...' : 'Reprocess'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">Total</td>
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                  <span className={status.total > 0 ? 'text-amber-600' : 'text-gray-400'}>
                    {status.total}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  {requeueAllState && requeueAllState !== 'loading' ? (
                    <span className={requeueAllState.startsWith('Error') ? 'text-red-600' : 'text-green-600'}>
                      {requeueAllState}
                    </span>
                  ) : (
                    <button
                      onClick={handleRequeueAll}
                      disabled={status.total === 0 || anyBusy}
                      className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {requeueAllState === 'loading' ? 'Queuing...' : 'Reprocess All'}
                    </button>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}

      <p className="mt-4 text-xs text-gray-500">
        Shows .zip files currently in the source S3 bucket under <code className="font-mono">hp_output/</code>.
        Files here were not yet delivered to their press (new arrivals or failed jobs whose source was not deleted).
        Reprocessing sends them back to the job queue.
      </p>
    </>
  );
};

export default MonitoringPage;
